if (!customElements.get('blog-filter')) {
  class BlogFilter extends HTMLElement {
    constructor() {
      super();
      this.handleDropdownChange = this.handleDropdownChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.filterDropdown = this.querySelector('#blog-filter-dropdown');
      this.articleCount = Number.parseInt(this.dataset.articleCount || '0', 10);
      this.pageSize = Number.parseInt(this.dataset.pageSize || '13', 10);
      this.selectedCollection = this.dataset.selectedCollection || '';
      this.blogRoot = this.closest('.main-blog');
      this.statusElement = this.blogRoot && this.blogRoot.querySelector('[data-blog-filter-status]');
      this.resultsShell = this.blogRoot && this.blogRoot.querySelector('[data-blog-results-shell]');
      this.resultsList = this.resultsShell && this.resultsShell.querySelector('[data-blog-results]');

      if (this.filterDropdown) {
        this.filterDropdown.addEventListener('change', this.handleDropdownChange);
      }

      // Shopify only exposes 50 blog articles to an ordinary Liquid loop. For larger
      // blogs, progressively build a complete index from the section's paginated HTML.
      // The article collection-reference metafield remains the source of truth.
      if (this.articleCount > 50) {
        if (this.selectedCollection) {
          this.renderCompleteFilteredResults();
        } else {
          const buildIndex = () => this.loadArticleIndex()
            .then((cards) => this.enhanceFilterOptions(cards))
            .catch(() => {});

          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(buildIndex, { timeout: 2500 });
          } else {
            window.setTimeout(buildIndex, 800);
          }
        }
      }
    }

    disconnectedCallback() {
      if (this.filterDropdown) {
        this.filterDropdown.removeEventListener('change', this.handleDropdownChange);
      }
      this.initialized = false;
    }

    handleDropdownChange(evt) {
      const url = new URL(this.dataset.blogUrl || window.location.href, window.location.origin);
      const selectedValue = evt.detail.selectedValue || '';

      if (selectedValue) {
        url.searchParams.set('collection', selectedValue);
      } else {
        url.searchParams.delete('collection');
      }

      window.location.href = url.toString();
    }

    setStatus(message) {
      if (!this.statusElement) return;

      this.statusElement.textContent = message || '';
      this.statusElement.hidden = !message;
    }

    buildIndexUrl(page) {
      const currentUrl = new URL(window.location.href);
      const blogUrl = new URL(this.dataset.blogUrl, window.location.origin);

      currentUrl.pathname = blogUrl.pathname;
      currentUrl.searchParams.delete('collection');
      currentUrl.searchParams.set('filter_index', '1');
      currentUrl.searchParams.set('section_id', this.dataset.sectionId);
      currentUrl.searchParams.set('page', String(page));
      currentUrl.hash = '';

      return currentUrl.toString();
    }

    async fetchIndexPage(page) {
      let lastError;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(this.buildIndexUrl(page), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) {
            throw new Error(`Blog index request failed with ${response.status}`);
          }

          const html = await response.text();
          const template = document.createElement('template');
          template.innerHTML = html;

          // The featured card is indexed too. This keeps the index complete even if
          // a Section Rendering response does not expose the private filter_index flag.
          return Array.from(template.content.querySelectorAll('[data-blog-article]')).map((card) => {
            if (card.tagName === 'LI') return card;

            const item = document.createElement('li');
            const articleCard = card.querySelector('.card--article');
            item.className = 'js-pagination-result';
            item.dataset.blogArticle = '';
            item.dataset.articleUrl = card.dataset.articleUrl || '';
            item.dataset.collectionHandles = card.dataset.collectionHandles || '';
            if (articleCard) item.appendChild(articleCard.cloneNode(true));
            return item;
          }).filter((card) => card.dataset.articleUrl && card.firstElementChild);
        } catch (error) {
          lastError = error;
          if (attempt === 0) {
            await new Promise((resolve) => window.setTimeout(resolve, 250));
          }
        }
      }

      throw lastError;
    }

    loadArticleIndex() {
      if (this.indexPromise) return this.indexPromise;

      const pageCount = Math.max(1, Math.ceil(this.articleCount / this.pageSize));
      const pages = new Array(pageCount);
      let nextPage = 1;
      const fetchWorker = async () => {
        while (nextPage <= pageCount) {
          const page = nextPage;
          nextPage += 1;
          pages[page - 1] = await this.fetchIndexPage(page);
        }
      };
      const workers = Array.from({ length: Math.min(3, pageCount) }, () => fetchWorker());

      this.indexPromise = Promise.all(workers).then(() => {
        const articleUrls = new Set();
        const cards = [];

        pages.flat().forEach((card) => {
          const articleUrl = card.dataset.articleUrl;
          if (!articleUrl || articleUrls.has(articleUrl)) return;

          articleUrls.add(articleUrl);
          cards.push(card);
        });

        return cards;
      });

      return this.indexPromise;
    }

    getCollectionOptions(cards) {
      const options = new Map();

      cards.forEach((card) => {
        card.querySelectorAll('[data-collection-handle]').forEach((link) => {
          const handle = link.dataset.collectionHandle;
          if (handle && !options.has(handle)) {
            options.set(handle, link.dataset.collectionName || link.textContent.trim() || handle);
          }
        });
      });

      return options;
    }

    enhanceFilterOptions(cards) {
      const options = this.getCollectionOptions(cards);
      const desktopList = this.querySelector('#blog-filter-links');
      const customSelect = this.querySelector('#blog_collections');
      const listbox = customSelect && customSelect.querySelector('.custom-select__listbox');

      options.forEach((name, handle) => {
        const desktopOption = desktopList && desktopList.querySelector(`[data-collection-handle="${CSS.escape(handle)}"]`);
        const mobileOption = listbox && listbox.querySelector(`[data-value="${CSS.escape(handle)}"]`);

        if (desktopOption) {
          desktopOption.dataset.collectionName = name;
          desktopOption.textContent = name;
        } else if (desktopList) {
          const item = document.createElement('li');
          const link = document.createElement('a');
          const url = new URL(this.dataset.blogUrl, window.location.origin);

          url.searchParams.set('collection', handle);
          link.href = url.toString();
          link.dataset.collectionHandle = handle;
          link.dataset.collectionName = name;
          link.className = 'pt-2 pb-2 pl-4 pr-4 text-theme-text block';
          link.textContent = name;
          item.appendChild(link);
          desktopList.appendChild(item);
        }

        if (mobileOption) {
          mobileOption.querySelector('span').textContent = name;
        } else if (listbox) {
          const option = document.createElement('li');
          const label = document.createElement('span');

          option.className = 'custom-select__option flex items-center js-option';
          option.id = `${customSelect.id}-opt-${listbox.children.length}`;
          option.setAttribute('role', 'option');
          option.dataset.value = handle;
          label.className = 'pointer-events-none';
          label.textContent = name;
          option.appendChild(label);
          listbox.appendChild(option);
        }

        if (customSelect && handle === this.selectedCollection) {
          const buttonLabel = customSelect.querySelector('.custom-select__btn > span');
          if (buttonLabel) buttonLabel.textContent = name;
        }
      });

      if (customSelect) {
        customSelect.options = customSelect.querySelectorAll('.custom-select__option');
      }
    }

    appendResultCards(cards, startIndex, count) {
      const fragment = document.createDocumentFragment();
      cards.slice(startIndex, startIndex + count).forEach((card) => fragment.appendChild(card.cloneNode(true)));
      this.resultsList.appendChild(fragment);
    }

    addShowMoreButton(cards, visibleCount) {
      const existingButton = this.resultsShell.querySelector('[data-blog-show-more]');
      if (existingButton) existingButton.remove();
      if (visibleCount >= cards.length) return;

      const wrapper = document.createElement('div');
      const button = document.createElement('button');
      let renderedCount = visibleCount;

      wrapper.className = 'main-blog__show-more mt-12 text-center';
      wrapper.dataset.blogShowMore = '';
      button.type = 'button';
      button.className = 'btn btn--secondary';
      button.textContent = this.dataset.showMoreLabel || 'Visa fler';
      wrapper.appendChild(button);
      this.resultsShell.appendChild(wrapper);

      button.addEventListener('click', () => {
        this.appendResultCards(cards, renderedCount, this.pageSize);
        renderedCount += this.pageSize;
        if (renderedCount >= cards.length) wrapper.remove();
      });
    }

    async renderCompleteFilteredResults() {
      if (!this.resultsList || !this.resultsShell) return;

      this.setStatus(this.dataset.loadingLabel || 'Laddar relevanta artiklar…');

      try {
        const cards = await this.loadArticleIndex();
        this.enhanceFilterOptions(cards);

        const matchingCards = cards.filter((card) => {
          const handles = (card.dataset.collectionHandles || '').split(',').filter(Boolean);
          return handles.includes(this.selectedCollection);
        });

        this.resultsList.replaceChildren();
        this.resultsShell.querySelector('[data-blog-empty-state]')?.remove();
        this.resultsShell.querySelector('[data-blog-server-pagination]')?.remove();

        if (matchingCards.length === 0) {
          const emptyState = document.createElement('p');
          emptyState.className = 'mt-8';
          emptyState.dataset.blogEmptyState = '';
          emptyState.textContent = this.dataset.emptyLabel || 'Inga artiklar hittades för det här användningsområdet.';
          this.resultsShell.appendChild(emptyState);
        } else {
          const visibleCount = Math.min(this.pageSize, matchingCards.length);
          this.appendResultCards(matchingCards, 0, visibleCount);
          this.addShowMoreButton(matchingCards, visibleCount);
        }

        this.setStatus('');
      } catch (error) {
        // Keep the server-rendered batch and pagination as a functional fallback.
        this.setStatus(this.dataset.errorLabel || 'Alla träffar kunde inte laddas. Använd sidnumreringen för att se fler.');
      }
    }
  }

  customElements.define('blog-filter', BlogFilter);
}
